/**
 * AppHeader component - shared navigation header for all applications
 *
 * Displays user name and logout button, generates navigation links based on
 * user roles, and highlights the current application in the nav.
 *
 * Validates: Requirements 6.1, 6.2, 6.3, 6.4
 */

import React, { useMemo } from 'react';
import {
  HomeRegular,
  ChatRegular,
  DocumentSearchRegular,
  SettingsRegular,
  SignOutRegular,
} from '@fluentui/react-icons';
import { usePermissions } from '../../auth/usePermissions';
import type { AppRole } from '../../auth/types';
import styles from './AppHeader.module.css';
import CAALogo from '../../assets/caa-logo.svg';

/**
 * Application identifier for determining current app highlighting
 */
export type CurrentApp = 'landing' | 'admin' | 'policy_navigator' | 'evidence_extractor';

export interface AppHeaderProps {
  /** Current application for nav highlighting */
  currentApp: CurrentApp;
  /** Optional action buttons to render in the header (between nav and user section) */
  actions?: React.ReactNode;
}

/**
 * Navigation item configuration
 */
export interface NavItemConfig {
  key: string;
  label: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  requiredRole?: AppRole;
}

/**
 * Generates navigation items based on user roles
 * Pure function for testability
 *
 * @param hasRole - Function to check if user has a specific role
 * @returns Array of navigation items the user can access
 */
export function generateNavItems(
  hasRole: (role: AppRole) => boolean
): NavItemConfig[] {
  const items: NavItemConfig[] = [
    {
      key: 'landing',
      label: 'Dashboard',
      href: '/',
      icon: HomeRegular,
    },
  ];

  if (hasRole('policy_navigator')) {
    items.push({
      key: 'policy_navigator',
      label: 'Policy Navigator',
      href: '/policy-navigator',
      icon: ChatRegular,
      requiredRole: 'policy_navigator',
    });
  }

  if (hasRole('evidence_extractor')) {
    items.push({
      key: 'evidence_extractor',
      label: 'Evidence Extractor',
      href: '/evidence-extractor',
      icon: DocumentSearchRegular,
      requiredRole: 'evidence_extractor',
    });
  }

  if (hasRole('admin')) {
    items.push({
      key: 'admin',
      label: 'Admin',
      href: '/admin',
      icon: SettingsRegular,
      requiredRole: 'admin',
    });
  }

  return items;
}

/**
 * Validates header content based on user roles
 * Pure function for property-based testing
 *
 * @param userRoles - Array of roles the user has
 * @param navItems - Generated navigation items
 * @param hasAdminLink - Whether admin link is present
 * @returns Object with validation results
 */
export function validateHeaderContent(
  userRoles: AppRole[],
  navItems: NavItemConfig[],
  hasAdminLink: boolean
): {
  hasOnlyAuthorizedLinks: boolean;
  adminLinkCorrect: boolean;
} {
  // Check that all nav items (except dashboard) correspond to user roles
  const roleBasedItems = navItems.filter((item) => item.requiredRole);
  const hasOnlyAuthorizedLinks = roleBasedItems.every(
    (item) => item.requiredRole && userRoles.includes(item.requiredRole)
  );

  // Admin link should be present if and only if user has admin role
  const userHasAdmin = userRoles.includes('admin');
  const adminLinkCorrect = hasAdminLink === userHasAdmin;

  return {
    hasOnlyAuthorizedLinks,
    adminLinkCorrect,
  };
}

/**
 * Shared navigation header component
 *
 * Features:
 * - Displays user name and logout button (Req 6.4)
 * - Generates navigation links based on user roles (Req 6.1)
 * - Highlights current application in nav
 * - Shows admin link only for admin users (Req 6.3)
 * - Home/dashboard link always available (Req 6.2)
 */
export const AppHeader: React.FC<AppHeaderProps> = ({ currentApp, actions }) => {
  const { user, hasRole, logout } = usePermissions();

  const navItems = useMemo(() => generateNavItems(hasRole), [hasRole]);

  return (
    <header className={styles.header} role="banner">
      <div className={styles.headerContainer}>
        <div className={styles.leftSection}>
          <img src={CAALogo} className={styles.headerLogo} alt="UK Civil Aviation Authority" />
          <nav className={styles.nav} aria-label="Main navigation">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = currentApp === item.key;
              return (
                <a
                  key={item.key}
                  href={item.href}
                  className={`${styles.navLink} ${isActive ? styles.active : ''}`}
                  aria-current={isActive ? 'page' : undefined}
                >
                  <Icon />
                  <span>{item.label}</span>
                </a>
              );
            })}
          </nav>
        </div>
        <div className={styles.userSection}>
          {actions}
          {user && (user.name || user.email) && (
            <span className={styles.userName} data-testid="user-name">
              {user.name || user.email}
            </span>
          )}
          <button
            className={styles.logoutButton}
            onClick={logout}
            aria-label="Logout"
            data-testid="logout-button"
          >
            <SignOutRegular />
            <span>Logout</span>
          </button>
        </div>
      </div>
    </header>
  );
};
